import * as React from 'react';

import ButtonBar, { ButtonBarProps } from 'components/molecules/ButtonBar';
import { getHealthGoalDisplayStatus } from 'components/molecules/HealthGoal/get-display-status';
import getGoalSelectionType, { identifyGoal, isUnderWeightByBMI } from 'components/molecules/HealthGoal/goal-selection';
import HealthGoalSelection, {
	getGoalFromHealthGoalTypes,
	getHealthGoalTypesFromGoal,
	HealthGoalItemWithoutType,
	TGoal,
} from 'components/molecules/HealthGoalSelection';
import { HealthGoalItem } from 'components/molecules/HealthGoalSelection';
import { AnamnesisStepResolver } from 'components/organisms/Anamnesis';
import { getHealthGoalRequiredAgreement } from 'components/organisms/Anamnesis/get-health-goal-required-agreement';
import { routes } from 'defs';
import { RouteType } from 'defs/routes';
import noop from 'helper/noop';
import { actionCreators, getTempHealthGoal } from 'store/profile';
import { nullToUndefined } from 'utils/null-to-undefined';
import { ArgumentTypes } from 'utils/types';

export type ReduceWeightTargetType = {
	recommendedReduceWeight: number | null;
	recommendedEnd: number | null;
	recommendedStart: number | null;
};

type DefineHealthTargetProps = {
	actionButtonsActive: ButtonBarProps;
	actionButtonsInactive: ButtonBarProps;
	tempHealthGoal: ReturnType<typeof getTempHealthGoal>;
	bmiValue: number;
	goals: HealthGoalItemWithoutType[];
	size?: number;
	weight?: number;
	isPregnant?: boolean;
	reduceWeightTarget: ReduceWeightTargetType;
	isUpdatingAnamnesis: boolean;
	onNavigationClick: (route: RouteType) => void;
	onStepCompleted: (
		resolver: AnamnesisStepResolver<
			ArgumentTypes<typeof actionCreators.doSaveHealthTarget | typeof actionCreators.doSaveAcceptHealthTarget>[0]
		>
	) => void;
	doSaveHealthTarget: typeof actionCreators.doSaveHealthTarget;
	doSaveAcceptHealthTarget: typeof actionCreators.doSaveAcceptHealthTarget;
};

type DefineHealthTargetState = {
	goals: HealthGoalItem[];
};

function getGoalsWithType({
	goals,
	selectedGoal,
	isPregnant,
	bmiValue,
}: {
	goals: HealthGoalItemWithoutType[];
	selectedGoal?: TGoal;
	isPregnant?: boolean;
	bmiValue: number;
}) {
	const enrichedGoals: HealthGoalItem[] = [];
	goals.forEach((goal) => {
		const goalSelectionType = getGoalSelectionType({
			isPregnant,
			bmiValue,
			goal,
			selectedGoal,
		});
		const goalType = identifyGoal(goalSelectionType);
		enrichedGoals.push({ ...goal, goalType });
	});
	return enrichedGoals;
}

export default class DefineHealthTarget extends React.Component<DefineHealthTargetProps, DefineHealthTargetState> {
	public static displayName = 'AnamnesisDefineHealthTarget';
	public static defaultProps = {
		actionButtonsActive: {},
		actionButtonsInactive: {},
		goals: [],
		bmiValue: -1,
		isPregnant: false,
		size: 0,
		weight: 0,
		onNavigationClick: noop,
		onStepCompleted: noop,
		doSaveHealthTarget: noop,
		reduceWeightTarget: {},
	};

	constructor(props: DefineHealthTargetProps) {
		super(props);

		const selectedGoal = this.getInitialSelectedGoal(props);

		const goals = getGoalsWithType({
			isPregnant: props.isPregnant,
			selectedGoal,
			goals: props.goals,
			bmiValue: props.bmiValue,
		});

		this.state = {
			goals,
		};
	}

	public componentDidUpdate(prevProps: DefineHealthTargetProps) {
		const { isPregnant, bmiValue, tempHealthGoal } = prevProps;
		if (
			this.props.isPregnant !== isPregnant ||
			this.props.bmiValue !== bmiValue ||
			this.props.tempHealthGoal !== tempHealthGoal
		) {
			const selectedGoal = this.getInitialSelectedGoal(this.props);
			const goals = getGoalsWithType({
				isPregnant: this.props.isPregnant,
				selectedGoal,
				goals: this.props.goals,
				bmiValue: this.props.bmiValue,
			});

			this.setState({
				goals,
			});
		}
	}

	public getInitialSelectedGoal(props: DefineHealthTargetProps): undefined | TGoal {
		let selectedGoal = props.tempHealthGoal
			? getGoalFromHealthGoalTypes(nullToUndefined(props.tempHealthGoal))
			: undefined;

		const goals = getGoalsWithType({
			isPregnant: props.isPregnant,
			selectedGoal,
			goals: props.goals,
			bmiValue: props.bmiValue,
		});

		if (!selectedGoal) {
			const recommendedGoal = goals.find((goal) => goal.goalType.isRecommended);
			selectedGoal = recommendedGoal ? recommendedGoal.name : undefined;
		}

		return selectedGoal;
	}

	get selectedGoal(): undefined | TGoal {
		let selectedGoal: TGoal | undefined;
		this.state.goals.every((goal) => {
			if (goal.goalType.isSelected) {
				selectedGoal = goal.name;
				return false;
			}
			return true;
		});
		return selectedGoal;
	}

	public onHandleButtonClicks = (type: string) => {
		const {
			onNavigationClick,
			onStepCompleted,
			isPregnant,
			weight,
			size,
			bmiValue,
			reduceWeightTarget,
		} = this.props;
		const selectedGoal = this.selectedGoal;
		const requiredArgreement = getHealthGoalRequiredAgreement({
			isPregnant,
			selectedGoal,
			weightInput: bmiValue,
			weight,
			size,
			bmiValue,
		});
		const displayStatus = getHealthGoalDisplayStatus({
			isPregnant,
			bmiValue,
			selectedGoal,
		});

		if (type === 'completed' && typeof onStepCompleted === 'function') {
			// step accept health target not needed PULSWM-3817
			if (!requiredArgreement && !displayStatus) {
				const recommendedReduceWeight = reduceWeightTarget.recommendedReduceWeight || 0;
				return onStepCompleted.apply(null, [
					{
						waitFor: 'savedAcceptHealthTarget',
						actionCreator: this.props.doSaveAcceptHealthTarget,
						nextRoute: routes.anamnesis.selectDietaryHabits,
						data: {
							acceptedHealthGoalWarningOverweight: false,
							acceptedHealthGoalWarningPregnant: false,
							acceptedHealthGoalWarningUnderweight: false,
							healthGoal: getHealthGoalTypesFromGoal(selectedGoal),
							weightReduceGoal: recommendedReduceWeight,
						},
					},
				]);
			}
			return onStepCompleted.apply(null, [
				{
					waitFor: 'savedHealthTarget',
					actionCreator: this.props.doSaveHealthTarget,
					nextRoute: routes.anamnesis.acceptHealthTarget,
					data: {
						selectedHealthGoal: getHealthGoalTypesFromGoal(this.selectedGoal),
					},
				},
			]);
		}

		if (typeof onNavigationClick === 'function') {
			type = type === 'completed' ? 'next' : type;
			onNavigationClick.apply(this, [routes.anamnesis.determineWeight]);
		}
	};

	public onGoalChange = (goal: TGoal) => {
		this.setState({
			goals: getGoalsWithType({
				isPregnant: this.props.isPregnant,
				selectedGoal: goal,
				goals: this.props.goals,
				bmiValue: this.props.bmiValue,
			}),
		});
	};

	public renderButtons() {
		const { isUpdatingAnamnesis, isPregnant, bmiValue } = this.props;
		const isUnderWeight = isUnderWeightByBMI(bmiValue);
		const disabledGoalSelected = (isPregnant || isUnderWeight) && this.selectedGoal === 'fruits';
		return (
			<ButtonBar
				{...(isUpdatingAnamnesis || !this.selectedGoal || disabledGoalSelected
					? this.props.actionButtonsInactive
					: this.props.actionButtonsActive)}
				loading={isUpdatingAnamnesis}
				handleButtonClicks={this.onHandleButtonClicks}
			/>
		);
	}

	public renderHealthGoal() {
		const { reduceWeightTarget, bmiValue } = this.props;

		if (!reduceWeightTarget || typeof reduceWeightTarget.recommendedReduceWeight !== 'number') {
			return <div />;
		}

		return (
			<HealthGoalSelection
				selectedGoal={this.selectedGoal}
				goals={this.state.goals}
				bmiValue={bmiValue}
				isPregnant={this.props.isPregnant}
				onGoalChange={this.onGoalChange}
			/>
		);
	}

	public render() {
		return (
			<div>
				{this.renderHealthGoal()}
				<div className="o-anamnesis__button-bar">{this.renderButtons()}</div>
			</div>
		);
	}
}
